/**
 * Which half of the day an attendance record belongs to, and whether a person may record another.
 *
 * A school day has two marks: one on arrival and one on leaving. They are told apart by the clock,
 * not by the order the scans happened in -- a student who scans twice before lunch has scanned
 * twice in the morning, and the second one is a mistake, not a departure. Ordering by sequence
 * would silently stamp 08:15 as the time they went home.
 *
 * The cut-off is per school because the boundary is a timetable decision, not a fact about time.
 * It is stored as "HH:MM" in `metadata/attendance_settings`.
 *
 * No React and no Firestore here: this decides things, so it has to be testable on its own.
 */

/** The stored value on `attendance_record.session`. Never translated -- only the shown word is. */
export const DAY_HALF = { IN: 'in', OUT: 'out' };

export const DEFAULT_CUTOFF = '12:00';

/** Minutes since midnight, or null when the input is not a time this can read. */
export const minutesOfDay = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

/** The local wall-clock time of an ISO timestamp, as minutes since midnight. */
export const minutesOfTimestamp = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
};

/**
 * Which half a moment falls in. An unreadable cut-off falls back to the default rather than
 * throwing: a school that has typed something odd into settings should still be able to take
 * attendance, and the default is the boundary they would have picked anyway.
 */
export const halfOf = (iso, cutoff = DEFAULT_CUTOFF) => {
  const at = minutesOfTimestamp(iso);
  const edge = minutesOfDay(cutoff) ?? minutesOfDay(DEFAULT_CUTOFF);
  if (at === null) return DAY_HALF.IN;
  return at < edge ? DAY_HALF.IN : DAY_HALF.OUT;
};

/**
 * The half a record counts as.
 *
 * Records written before this existed carry no `session`. They are all arrivals -- there was no
 * way to record anything else -- so they read as IN rather than being re-derived from their
 * timestamp, which would turn every afternoon check-in in the history into a departure.
 */
export const halfOfRecord = (record) => (
  record?.session === DAY_HALF.OUT ? DAY_HALF.OUT : DAY_HALF.IN
);

/**
 * Whether this person may record `half` today, given the records they already have for the day.
 *
 * Returns the record that blocks it, or null when they may go ahead. The caller shows the time off
 * the blocking record: "you already signed in at 08:12" is answerable, "already signed in" is not.
 */
export const blockingRecord = (recordsToday, half) => (
  (recordsToday || []).find(r => halfOfRecord(r) === half) || null
);

/** The two marks for one person on one day, either of which may be missing. */
export const dayMarks = (recordsToday) => {
  const list = [...(recordsToday || [])].sort(
    (a, b) => String(a.submittedAt || '').localeCompare(String(b.submittedAt || '')),
  );
  return {
    in: list.find(r => halfOfRecord(r) === DAY_HALF.IN) || null,
    out: list.find(r => halfOfRecord(r) === DAY_HALF.OUT) || null,
  };
};

/**
 * The one stable name for a person across every attendance path.
 *
 * It has to agree between the three writers, or the same person gets two different ids in one half
 * and the duplicate this whole scheme prevents comes straight back. Students are keyed by SID and
 * staff by address, because that is what each path reliably carries: a card issued to a student
 * always has the SID, and may have no address on file at all.
 */
export const personKeyOf = (record) => {
  const sid = String(record?.submittedBySid || '').trim();
  const email = String(record?.submittedByEmail || '').trim().toLowerCase();
  const isStudent = String(record?.submittedByRole || record?.role || '').toLowerCase() === 'student';
  if (isStudent) return sid ? `sid:${sid}` : email;
  return email || (sid ? `sid:${sid}` : '');
};

/**
 * The document id for a record: one document per person per half per day.
 *
 * This is what makes a duplicate impossible rather than merely guarded against -- a second write
 * lands on the same document, and the rules refuse it for anyone who may not update. Returns null
 * when there is nothing stable to key on, and the caller falls back to a generated id rather than
 * inventing a key and silently merging two people.
 */
export const attendanceDocId = (record) => {
  const who = personKeyOf(record);
  const date = String(record?.submittedDateLocal || '').trim();
  if (!who || !date) return null;
  // `/` would split the path into another collection; nothing else in an id needs escaping.
  return `${date}__${halfOfRecord(record)}__${who}`.replace(/\//g, '_');
};
